import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
/**
 * The single, app-wide pre-content loading indicator: a brand ring on a plain
 * backdrop, no copy. Shared by the route-chunk Suspense fallback and the App
 * auth gate so every "still loading" state looks identical (no white→dark
 * handoff between them).
 *
 * Deliberately styled with inline styles (not Tailwind/theme tokens) so it
 * renders correctly even before the app's CSS/theme has loaded — e.g. as a
 * lazy-route Suspense fallback.
 *
 * That is also why the theme is read straight from localStorage rather than
 * from `useDashTheme`: the backdrop used to be hard-coded white, so toggling to
 * dark (or any lazy route on a dark session) flashed a full-screen white panel.
 * Same storage key as useDashTheme, and it listens to the same event so a
 * toggle mid-load repaints instead of finishing in the old theme.
 */
const STORAGE_KEY = 'dashboard_theme';
const PALETTE = {
    light: { bg: '#ffffff', track: '#e2e8f0', head: '#324ECF' },
    dark: { bg: '#0a0c10', track: 'rgba(255, 255, 255, 0.12)', head: '#3b82f6' },
};
const readTheme = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'dark' || saved === 'light')
            return saved;
    }
    catch (_a) {
        /* storage unavailable — fall through to default */
    }
    return 'light';
};
export const FullScreenLoader = () => {
    const [theme, setTheme] = useState(readTheme);
    useEffect(() => {
        const onChange = (e) => {
            const next = e.detail;
            if (next === 'light' || next === 'dark')
                setTheme(next);
        };
        window.addEventListener('dash-theme-change', onChange);
        return () => window.removeEventListener('dash-theme-change', onChange);
    }, []);
    const colors = PALETTE[theme];
    return (_jsxs("div", { style: {
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
            background: colors.bg,
            zIndex: 50,
        }, children: [_jsx("span", { style: {
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: `4px solid ${colors.track}`,
                    borderTopColor: colors.head,
                    animation: 'vFullScreenLoaderSpin 1s linear infinite',
                } }), _jsx("style", { children: '@keyframes vFullScreenLoaderSpin { to { transform: rotate(360deg); } }' })] }));
};
export default FullScreenLoader;
